import { Button } from '@capsule/consumer-design-system';

import { useMountEffect } from '@react-hookz/web';
import { captureException } from '@sentry/react';
import classNames from 'classnames';
import map from 'lodash/map';
import { useHistory } from 'react-router';
import { css } from 'styled-components';
import InlineSVG from 'svg-inline-react';

import snowflakeSvg from 'public/snowflake.svg';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Card, CardItem } from 'src/app/components/Elements/Card';
import { CardRowText } from 'src/app/components/Elements/Card/CardRowText';
import InfoCardItem from 'src/app/components/Elements/Card/InfoCardItem';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import Spinner from 'src/app/components/Elements/Spinner';
import { Typography } from 'src/app/components/Elements/Typography';
import { OrderPriceBreakdown } from 'src/app/components/PageSections/OrderPriceBreakdown';
import { useCartDeleteItem } from 'src/app/hooks/data/cart';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { medications } from 'src/app/router/paths';
import { segmentTrack, cartRemoveItems, UPDATE_BFF_CART } from 'src/app/state/actions';
import { bffFetchCart } from 'src/app/state/actions/cart';
import {
  getBFFCart,
  getBffCartPrescriptionCartItems,
  getBFFCartSubtotal,
  getBFFCartTax,
  getBFFCartTip,
  getBFFCartTotal,
  getCartItems,
  getCurrentCartId,
  getIsBffCartFetching,
} from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import { CART_PRODUCT_TYPE } from 'src/app/types';
import { formatPrice } from 'src/app/utils';
import { REFRIGERATION_TYPES } from 'src/consts';

import { convertNumberToValueInCents } from '../utils';

import styles from './styles.module.scss';

const DeliveryItems = () => {
  const dispatch = useDispatch();
  const { replace } = useHistory();

  const stateCartItems = useSelector(getCartItems);
  const cartId = useSelector(getCurrentCartId);
  const bffCart = useSelector(getBFFCart);
  const isBffCartFetching = useSelector(getIsBffCartFetching);
  const tax = useSelector(getBFFCartTax);
  const tip = useSelector(getBFFCartTip);
  const subtotal = useSelector(getBFFCartSubtotal);
  const total = useSelector(getBFFCartTotal);
  const prescriptionCartItems = useSelector(getBffCartPrescriptionCartItems);

  const trackRemoveFromCartEvent = useSegmentEvent('removeFromCart', { shouldTrackOnMount: false });

  const {
    mutateAsync: mutateAsyncCartDeleteItem,
    isLoading: isDeletingSomeCartItem,
    variables: deleteCartVars,
  } = useCartDeleteItem();

  const checkIfDeletingCartItem = (itemId: string) => isDeletingSomeCartItem && deleteCartVars?.itemId === itemId;

  /**
   * Some context into why we are doing things like this...
   * We want to remove Redux and use react-query for data fetching.
   *
   * Unfortunately, it's a big task to remove Redux from all of checkout.
   *
   * Plus, we only want to add the ability to remove an item from the cart.
   *
   * So, we will JUST use react-query for the relevant mutation (deletion)
   * ...but we need to sync Redux state with the mutation response.
   *
   * Unfortunately, we track the data in 2 places in Redux... "Cart" and "BffCart".
   * Cart represents local state from a user adding items to their cart.
   * BffCart represents response data from the BFF when the cart is _actually_ created (upon checkout start).
   *
   * Even worse, Cart stores items by "otcItemId" and "fulfillmentId"
   * ...but BffCart stores items by "productId" (which it looks like is identical to "prescriptionId").
   *
   * So, we made it so addToCart requires productId/prescriptionId.
   *
   * This allows us to remove the item from the database's cart, Redux's BffCart, and Redux's Cart.
   */
  const deleteCartItem = async (payload: {
    cartId: string;
    dosage: string;
    isOTC: boolean;
    itemId: string;
    name: string;
    patientMedicationId?: string;
    fulfillmentId?: string;
    price: string | number;
    productId: string;
    quantity: number;
    essentialItemForFacilityUuid?: string;
  }) => {
    try {
      const newCart = await mutateAsyncCartDeleteItem({ cartId, itemId: payload.itemId });
      const relevantStateItem = stateCartItems.find(item => {
        if (payload.isOTC) {
          if (payload.essentialItemForFacilityUuid) {
            return item.otcItemId === payload.essentialItemForFacilityUuid;
          }
          return item.otcItemId === payload.productId;
        } else {
          return item.prescriptionId === payload.productId;
        }
      });

      dispatch(cartRemoveItems([relevantStateItem], cartId));
      dispatch({ type: UPDATE_BFF_CART, data: newCart });
      trackRemoveFromCartEvent({
        page: 'cart_list',
        display_name_primary: payload.name,
        dosage: payload.dosage,
        quantity: payload.quantity,
        OTC: payload.isOTC,
        patientmedication_id: payload.patientMedicationId ?? null,
        prescription_fulfillment_id: payload.fulfillmentId ?? null,
        price: payload.price,
      });

      if (newCart.otcCartItems?.length === 0 && newCart.prescriptionCartItems?.length === 0) {
        replace(medications.route);
      }
    } catch (error) {
      captureException(error);
    }
  };

  const otcItems = bffCart?.otcCartItems;
  const showPrice = bffCart?.showPrice ?? false;
  const otcCount = otcItems?.length ?? 0;
  const itemCount = prescriptionCartItems?.length ?? 0;

  const allCartItems = [];
  if (prescriptionCartItems?.length > 0) {
    prescriptionCartItems.forEach(item => allCartItems.push(item));
  }

  if (otcItems?.length > 0) {
    otcItems.forEach(item => allCartItems.push(item));
  }

  useMountEffect(() => {
    if (cartId && !bffCart && !isBffCartFetching) {
      dispatch(bffFetchCart(cartId));
    }

    const trackViewCartPayload = {
      subtotal: convertNumberToValueInCents(subtotal),
      otc_count: otcCount,
      item_count: itemCount,
      tip_amount: convertNumberToValueInCents(tip),
      total: convertNumberToValueInCents(total),
    };
    dispatch(segmentTrack(UNIFIED.viewCart, trackViewCartPayload));
  });

  return (
    <StandardPage title={'In your bag'}>
      {isBffCartFetching ? (
        <Spinner centered />
      ) : (
        <div className={styles.component}>
          <Card>
            {map(
              allCartItems,
              ({
                price,
                id: itemId,
                product: { id: productId, name, refrigerationType, type, dosage },
                quantity,
                essentialItemForFacilityUuid,
              }) => {
                const isDeletingCartItem = checkIfDeletingCartItem(itemId);
                const isOTC = type === CART_PRODUCT_TYPE.OTCItem;

                const relevantStateItem = stateCartItems.find(item => item.prescriptionId === productId);

                return refrigerationType !== REFRIGERATION_TYPES.no_refrigeration ? (
                  <InfoCardItem info="This medication requires refrigeration upon delivery.">
                    <div className={styles.row}>
                      <InlineSVG className={classNames(styles.snowflake)} src={snowflakeSvg} data-cy="snowflake" />
                      <div
                        className={classNames(styles.name, 'label', 'contrast', 'inspectlet-sensitive')}
                        data-cy="delivery-item-name"
                      >
                        {name}
                        {quantity > 1 ? ` (${quantity})` : ``}
                      </div>
                      <Typography variant={'body'}>{showPrice ? `${price}` : '—'}</Typography>
                    </div>

                    <div
                      className={styles.row}
                      css={css`
                        justify-content: flex-end;
                      `}
                    >
                      <Button
                        variant="text-md"
                        onClick={async () => {
                          await deleteCartItem({
                            cartId,
                            itemId,
                            productId,
                            dosage,
                            fulfillmentId: relevantStateItem?.fulfillmentId,
                            isOTC,
                            name,
                            patientMedicationId: relevantStateItem?.patientMedicationId,
                            price,
                            quantity,
                            essentialItemForFacilityUuid,
                          });
                        }}
                        isLoading={isDeletingCartItem}
                      >
                        REMOVE
                      </Button>
                    </div>
                  </InfoCardItem>
                ) : (
                  <CardItem className={styles.cardItem} key={productId}>
                    <div className={styles.row}>
                      <div
                        className={classNames(styles.name, 'label', 'contrast', 'inspectlet-sensitive')}
                        data-cy="delivery-item-name"
                      >
                        {name}
                        {quantity > 1 ? ` (${quantity})` : ``}
                      </div>
                      <Typography variant={'body'}>{showPrice ? `${price}` : '—'}</Typography>
                    </div>

                    <div
                      className={styles.row}
                      css={css`
                        justify-content: flex-end;
                      `}
                    >
                      <Button
                        variant="text-md"
                        onClick={async () => {
                          await deleteCartItem({
                            cartId,
                            itemId,
                            productId,
                            dosage,
                            fulfillmentId: relevantStateItem?.fulfillmentId,
                            isOTC,
                            name,
                            patientMedicationId: relevantStateItem?.patientMedicationId,
                            price,
                            quantity,
                            essentialItemForFacilityUuid,
                          });
                        }}
                        isLoading={isDeletingCartItem}
                      >
                        REMOVE
                      </Button>
                    </div>
                  </CardItem>
                );
              }
            )}
          </Card>
          <Card>
            <OrderPriceBreakdown tip={tip} subtotal={subtotal} tax={tax} noDivider={false} showPrice={showPrice} />
            <CardItem>
              <CardRowText
                data_cy={'order-total'}
                leftText={'Total'}
                rightText={showPrice ? formatPrice(total) : '—'}
                leftTextVariant={'bodyBold'}
                rightTextVariant={'bodyBold'}
              />
            </CardItem>
          </Card>
        </div>
      )}
    </StandardPage>
  );
};

export default DeliveryItems;
